import React, { useState } from 'react';
import { getAuth, reauthenticateWithCredential, EmailAuthProvider, updatePassword } from 'firebase/auth';
import { auth } from '../firebase'; // Ensure this points to your Firebase config
import { FaEye, FaEyeSlash, FaLock } from 'react-icons/fa';
import { makeStyles } from '@mui/styles';

// Create styles using makeStyles
const useStyles = makeStyles({
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    backgroundColor: '#f4f6f9',
    padding: 10,
  },
  formContainer: {
    backgroundColor: '#fff',
    boxShadow: '0 6px 12px rgba(0, 0, 0, 0.1)',
    borderRadius: 12,
    padding: 80,
    width: '100%',
    maxWidth: 400,
  },
  headerContainer: {
    textAlign: 'center',
    marginBottom: 20,
  },
  profileIcon: {
    fontSize: 48,
    color: '#013a64',
    marginBottom: 12,
  },
  title: {
    fontSize: 24,
    fontWeight: '600',
    color: '#013a64',
  },
  inputWrapper: {
    position: 'relative',
    marginBottom: 20,
  },
  input: {
    width: '100%',
    padding: '12px 12px',
    borderRadius: 10,
    border: '1px solid #dcdcdc',
    fontSize: 16,
    color: '#333',
    backgroundColor: '#f9f9f9',
    transition: 'border-color 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
    '&:focus': {
      borderColor: '#013a64',
      boxShadow: '0 0 5px rgba(1, 58, 100, 0.2)',
    },
  },
  icon: {
    position: 'absolute',
    top: '50%',
    right: 15,
    transform: 'translateY(-50%)',
    cursor: 'pointer',
  },
  button: {
    backgroundColor: '#013a64',
    color: '#fff',
    padding: '14px 0',
    borderRadius: 10,
    fontSize: 16,
    fontWeight: '600',
    width: '100%',
    cursor: 'pointer',
    border: 'none',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    transition: 'background-color 0.3s ease, box-shadow 0.3s ease',
    '&:hover': {
      backgroundColor: '#015f8f',
      boxShadow: '0 6px 12px rgba(0, 0, 0, 0.2)',
    },
    '&:disabled': {
      backgroundColor: '#dcdcdc',
      cursor: 'not-allowed',
    },
  },
});

const ChangePasswordScreen = () => {
  const classes = useStyles();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const togglePasswordVisibility = () => setShowPassword(!showPassword);

  const handlePasswordChange = async () => {
    if (newPassword !== confirmPassword) {
      alert('New passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      alert('Password should be at least 8 characters long');
      return;
    }

    const user = auth.currentUser;
    if (!user) {
      alert('User not authenticated');
      return;
    }

    try {
      setLoading(true);
      const credentials = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credentials);
      await updatePassword(user, newPassword);
      alert('Password changed successfully');
    } catch (error) {
      alert(error.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={classes.container}>
      <div className={classes.formContainer}>
        <div className={classes.headerContainer}>
          <FaLock className={classes.profileIcon} />
          <h2 className={classes.title}>Change Password</h2>
        </div>
        <div className={classes.inputWrapper}>
          <input
            type={showPassword ? 'text' : 'password'}
            placeholder="Current Password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            className={classes.input}
          />
        </div>
        <div className={classes.inputWrapper}>
          <input
            type={showPassword ? 'text' : 'password'}
            placeholder="New Password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className={classes.input}
          />
        </div>
        <div className={classes.inputWrapper}>
          <input
            type={showPassword ? 'text' : 'password'}
            placeholder="Confirm New Password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className={classes.input}
          />
          <span className={classes.icon} onClick={togglePasswordVisibility}>
            {showPassword ? <FaEyeSlash className="text-gray-600" /> : <FaEye className="text-gray-600" />}
          </span>
        </div>
        <button
          onClick={handlePasswordChange}
          className={classes.button}
          disabled={loading}
        >
          {loading ? 'Changing...' : 'Change Password'}
        </button>
      </div>
    </div>
  );
};

export default ChangePasswordScreen;
